import { useState, useEffect } from 'react';
import type { RedshiftExportJob } from '@atlas/shared';
import {
  NewRedshiftExportForm,
  MAPPING_BUCKET,
  type RedshiftExportMode,
} from '../components/NewRedshiftExportForm.js';
import { RedshiftExportsTable } from '../components/RedshiftExportsTable.js';
import { subscribeToRedshiftExports } from '../lib/redshiftExports.js';

export function RedshiftExportPage() {
  const [exports, setExports] = useState<Array<RedshiftExportJob & { id: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<RedshiftExportMode>('csvs');

  useEffect(() => {
    return subscribeToRedshiftExports(
      (list) => {
        setExports(list);
        setLoading(false);
        setLoadError(null);
      },
      (err) => {
        setLoadError(err.message);
        setLoading(false);
      },
    );
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#37352f] tracking-tight">
          Redshift export
        </h1>
        <p className="text-sm text-[#73726e] mt-1">
          Unload a Redshift schema to S3 as CSV. Redshift writes the files
          directly, so run time depends on the warehouse rather than on this app.
        </p>
      </div>

      <div className="max-w-xl mb-10">
        {/* The form self-filters collision checks by bucket, so it gets every
            run; only the log below is scoped to the selected mode. */}
        <NewRedshiftExportForm
          existingExports={exports}
          mode={mode}
          onModeChange={setMode}
        />
      </div>

      {loadError && (
        <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2 mb-4">
          Could not load runs: {loadError}
        </p>
      )}

      {(loading || exports.length > 0) && (
        <RedshiftExportsTable
          exports={exports.filter((e) =>
            mode === 'mapping'
              ? e.s3Bucket === MAPPING_BUCKET
              : e.s3Bucket !== MAPPING_BUCKET,
          )}
          loading={loading}
          emptyMessage={
            mode === 'mapping'
              ? 'No files-mapping exports yet.'
              : 'No migration CSV exports yet.'
          }
        />
      )}
    </div>
  );
}
